import React from "react";
import { AlertTriangle, ArrowRight, Loader2, Clock, MapPin, Phone, Mail, Calendar, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Feasibility verdict UI. Renders an actionable panel when same-day delivery
 * cannot realistically be made.
 *
 * Props:
 *   verdict     ('ok'|'manual_approval'|'next_day_only'|'too_late')
 *   reason      (string)
 *   data        (full feasibility response — alternatives etc)
 *   form        (current booking form — for urgent-request prefill)
 *   onPickNextDay({date, window})  — caller updates pickup_date + pickup_time and refreshes quote
 *   onUrgentSubmitted()            — after urgent request sent
 */
export default function FeasibilityPanel({ verdict, reason, data, form, onPickNextDay, onUrgentSubmitted }) {
  const [tab, setTab] = React.useState("next_day");  // next_day | urgent | contact
  const [pickedWindow, setPickedWindow] = React.useState(null);
  const [urgentNote, setUrgentNote] = React.useState("");
  const [urgentBusy, setUrgentBusy] = React.useState(false);
  const [urgentSent, setUrgentSent] = React.useState(false);

  if (!verdict || verdict === "ok") return null;

  const nextDayAlt = (data?.alternatives || []).find((a) => a.type === "next_day");
  const isTooLate = verdict === "too_late" || verdict === "next_day_only";
  const isManual = verdict === "manual_approval";

  const headlineCopy = isManual
    ? "Denne levering kræver manuel godkendelse"
    : "Denne levering kan desværre ikke nås i dag";

  const subCopy = isManual
    ? "Send forespørgslen, så kontakter EMA Kurer dig hurtigst muligt med pris og mulighed."
    : "Vælg næste ledige leveringsdag eller kontakt os for akut express-aftale.";

  const submitNextDay = () => {
    if (!pickedWindow || !nextDayAlt) return;
    onPickNextDay?.({ date: nextDayAlt.date, window: pickedWindow });
    toast.success(`Skifter til ${nextDayAlt.label} · ${pickedWindow.label}`);
  };

  const sendUrgent = async () => {
    setUrgentBusy(true);
    try {
      const name = `${form.customer_first_name || ""} ${form.customer_last_name || ""}`.trim() || "—";
      await api.post("/booking/urgent-request", {
        customer_name: name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone || "—",
        pickup_address: form.pickup_address,
        pickup_zip: form.pickup_zip,
        pickup_city: form.pickup_city,
        delivery_address: form.delivery_address,
        delivery_zip: form.delivery_zip,
        delivery_city: form.delivery_city,
        delivery_type: form.delivery_type,
        requested_pickup_time: form.pickup_time,
        distance_km: data?.distance_km || null,
        drive_time_min: data?.drive_time_min || null,
        note: urgentNote || null,
      });
      setUrgentSent(true);
      onUrgentSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende forespørgsel");
    } finally { setUrgentBusy(false); }
  };

  return (
    <div className="bg-amber-50 border-2 border-amber-500" data-testid={`feasibility-${verdict}`}>
      {/* Headline */}
      <div className="px-5 sm:px-7 py-5 border-b border-amber-300 bg-amber-100">
        <div className="flex items-start gap-3">
          <AlertTriangle size={22} className="text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display font-bold text-lg text-amber-900">{headlineCopy}</div>
            <p className="text-amber-900/85 text-sm mt-1">{reason || subCopy}</p>
            {data && (
              <div className="text-amber-900/70 text-xs mt-2 flex flex-wrap gap-x-4 gap-y-1">
                <span className="flex items-center gap-1"><MapPin size={11} /> {data.distance_km} km rute</span>
                <span className="flex items-center gap-1"><Clock size={11} /> ~{data.drive_time_min} min køretid</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3-tab alternatives */}
      <div className="grid grid-cols-3 border-b border-amber-300">
        {[
          { k: "next_day", label: "Book til i morgen", icon: Calendar, hide: isManual },
          { k: "urgent", label: "Send akut forespørgsel", icon: Send },
          { k: "contact", label: "Kontakt os", icon: Phone },
        ].filter((t) => !t.hide).map((t) => {
          const Icon = t.icon; const active = tab === t.k;
          return (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`text-xs py-3 px-2 sm:px-4 font-semibold uppercase tracking-widest border-r border-amber-300 last:border-0 flex flex-col sm:flex-row items-center gap-1.5 justify-center transition ${active ? "bg-white text-amber-900" : "text-amber-900/70 hover:bg-amber-100"}`}
              data-testid={`feas-tab-${t.k}`}
            >
              <Icon size={14} /> <span className="text-center leading-tight">{t.label}</span>
            </button>
          );
        })}
      </div>

      <div className="p-5 sm:p-7">
        {/* NEXT DAY */}
        {tab === "next_day" && nextDayAlt && (
          <div data-testid="feas-pane-next-day">
            <div className="kicker text-amber-700">Vælg leveringsdag og vindue</div>
            <div className="font-display font-semibold text-brand-dark mt-2">{nextDayAlt.label}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {nextDayAlt.windows.map((w) => {
                const sel = pickedWindow?.code === w.code;
                return (
                  <button
                    key={w.code}
                    onClick={() => setPickedWindow(w)}
                    className={`border p-3 text-sm transition ${sel ? "border-brand-red bg-brand-red text-white" : "border-amber-400 bg-white text-brand-dark hover:border-brand-red"}`}
                    data-testid={`feas-window-${w.code}`}
                  >
                    <div className="font-display font-semibold">{w.label}</div>
                    <div className={`text-[10px] uppercase tracking-widest mt-1 ${sel ? "text-white/70" : "text-brand-text2"}`}>vindue</div>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-brand-text2 mt-4">
              Når du vælger et dagtidsvindue, fjernes aften- og sen-aften-tillæg automatisk fra prisen.
            </p>
            <button
              onClick={submitNextDay}
              disabled={!pickedWindow}
              className="btn-primary mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="feas-next-day-confirm"
            >
              Brug dette tidspunkt <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* URGENT */}
        {tab === "urgent" && (
          <div data-testid="feas-pane-urgent">
            {!urgentSent ? (
              <>
                <div className="kicker text-amber-700">Send akut forespørgsel</div>
                <p className="text-sm text-brand-text2 mt-2 leading-relaxed">
                  Vi kontakter dig hurtigst muligt med pris og mulighed for akut levering — også uden for vores normale vindue.
                </p>
                <label className="label-flat mt-5">Beskriv hastighed/tidsbehov (valgfrit)</label>
                <textarea
                  rows={3}
                  className="input-flat"
                  placeholder="fx 'Skal nå et tog kl. 22:55 fra Nykøbing F St.'"
                  value={urgentNote}
                  onChange={(e) => setUrgentNote(e.target.value)}
                  data-testid="feas-urgent-note"
                />
                <div className="bg-brand-bg2 border border-brand p-4 mt-5 text-xs space-y-1.5 text-brand-text2">
                  <div className="font-semibold text-brand-dark uppercase tracking-widest text-[10px]">Vi sender:</div>
                  <div>· {form.customer_email || "(udfyld email på trin 3)"}</div>
                  <div>· {form.customer_phone || "(udfyld telefon på trin 3)"}</div>
                  <div>· {form.pickup_city || "?"} → {form.delivery_city || "?"} ({data?.distance_km} km)</div>
                </div>
                <button
                  onClick={sendUrgent}
                  disabled={urgentBusy || !form.customer_email || !form.customer_phone}
                  className="btn-primary mt-5 disabled:opacity-50"
                  data-testid="feas-urgent-submit"
                >
                  {urgentBusy ? <><Loader2 size={16} className="animate-spin" /> Sender...</> : <><Send size={16} /> Send forespørgsel</>}
                </button>
                {(!form.customer_email || !form.customer_phone) && (
                  <p className="text-xs text-amber-700 mt-3">Udfyld email og telefon på trin 3 først.</p>
                )}
              </>
            ) : (
              <div className="text-center py-6" data-testid="feas-urgent-sent">
                <div className="w-14 h-14 bg-emerald-100 border border-emerald-300 mx-auto flex items-center justify-center">
                  <CheckCircle2 size={28} className="text-emerald-700" />
                </div>
                <div className="font-display font-bold text-xl text-brand-dark mt-4">Forespørgsel sendt</div>
                <p className="text-sm text-brand-text2 mt-2 max-w-md mx-auto">
                  Vi kontakter dig på <strong>{form.customer_phone}</strong> hurtigst muligt med pris og mulighed.
                </p>
              </div>
            )}
          </div>
        )}

        {/* CONTACT */}
        {tab === "contact" && (
          <div className="space-y-4" data-testid="feas-pane-contact">
            <div className="kicker text-amber-700">Kontakt EMA Kurer direkte</div>
            <p className="text-sm text-brand-text2">
              Ring eller skriv så finder vi en løsning sammen — også uden for vores normale tider.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a href="tel:+4530309912" className="bg-brand-dark text-white p-5 hover:bg-brand-red transition flex items-center gap-3" data-testid="feas-contact-phone">
                <Phone size={22} className="text-brand-red" />
                <div>
                  <div className="kicker text-white/70">Ring</div>
                  <div className="font-display font-bold text-lg">+45 30 30 99 12</div>
                </div>
              </a>
              <a href="mailto:kontakt@emakurer.dk?subject=Akut%20kurérforespørgsel" className="bg-white border border-brand p-5 hover:border-brand-red transition flex items-center gap-3" data-testid="feas-contact-email">
                <Mail size={22} className="text-brand-red" />
                <div>
                  <div className="kicker">Skriv</div>
                  <div className="font-display font-bold text-brand-dark text-lg">kontakt@emakurer.dk</div>
                </div>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
